import { z } from 'zod/v4'
import { CRUDEvent } from 'skedyul-db/enums'

// ─────────────────────────────────────────────────────────────────────────────
// Activity Context Types (for tracking source of changes)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Source types for activity tracking.
 * Identifies where an instance operation originated from.
 */
export const ActivitySourceSchema = z.enum([
  'ui', // User action via tRPC (web UI)
  'api', // External API call (skedyul-node SDK)
  'cli', // CLI command (skedyul CLI tool)
  'workflow', // Workflow step execution
  'import', // CSV/data import
  'agent', // AI agent action
  'batch_job', // Batch job processing
  'system', // System-initiated (migrations, etc.)
])

export type ActivitySource = z.infer<typeof ActivitySourceSchema>

/**
 * Rich context object for activity tracking.
 * Replaces the ambiguous activitySessionId with structured data.
 */
export const ActivityContextSchema = z.object({
  // Required: identifies the source type
  source: ActivitySourceSchema,

  // Common: who initiated the action
  memberId: z.string().optional(),

  // API source identifiers
  appInstallationId: z.string().optional(),
  appVersionId: z.string().optional(),

  // Workflow source identifiers
  workflowRunId: z.string().optional(),
  workflowStepId: z.string().optional(),

  // Import source identifiers
  importId: z.string().optional(),
  importRowId: z.string().optional(),

  // Agent source identifiers
  agentRunId: z.string().optional(),
  conversationId: z.string().optional(),

  // Batch job source identifiers
  batchJobId: z.string().optional(),

  // Optional: human-readable description
  description: z.string().optional(),
})

export type ActivityContext = z.infer<typeof ActivityContextSchema>

// ─────────────────────────────────────────────────────────────────────────────
// Activity Record Types (for UI display)
// ─────────────────────────────────────────────────────────────────────────────

// Related data schemas
export const ActivityRecordSchema = z.object({
  id: z.string(),
  recordId: z.string(),
  field: z.object({
    id: z.string(),
    label: z.string(),
  }),
  fromData: z.unknown(),
  toData: z.unknown(),
})

// Main activity schema
export const ActivitySchema = z.object({
  id: z.string(),
  instance: z.object({
    id: z.string(),
    model: z.object({
      id: z.string(),
      name: z.string(),
      namePlural: z.string(),
    }),
  }),
  context: ActivityContextSchema,
  event: z.nativeEnum(CRUDEvent),
  meta: z.unknown(),
  createdAt: z.string(),
  updatedAt: z.string(),
  recordActivity: z.array(ActivityRecordSchema),
  // Add relationship information for better activity descriptions
  relationshipInfo: z
    .object({
      isRelationshipActivity: z.boolean(),
      cardinality: z.string().optional(),
      sourceModelName: z.string().optional(),
      targetModelName: z.string().optional(),
      relationshipFieldName: z.string().optional(),
    })
    .optional(),
})

export type Activity = z.infer<typeof ActivitySchema>

// Array of activities
export const ActivitiesSchema = z.array(ActivitySchema).optional().nullable()
